import { DecodedParamsRoute } from 'components/routing/DecodedParamsRoute';
import {
  useGetLedgerAccountQuery,
  useGetLedgerAccountsQuery,
  useGetLedgerEntryGroupQuery,
  useGetLedgerEntryQuery,
} from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import React, { Suspense, useCallback, useMemo } from 'react';
import { getResultOrError } from 'utils/errors';
import { makePath } from 'utils/routes';
import { Route, Router, Switch } from 'wouter';
import { useLedger } from 'wrappers/LedgerProvider/LedgerProvider';

import deterministicId from '@fragment/deterministic-id';
import { getLastPathElement, getSubpaths } from '@fragment/path-utils/path';
import { decodeLedgerId } from '@fragment/shared-utils/ids/ledger';
import { encodeLedgerAccountId } from '@fragment/shared-utils/ids/ledgerAccount';
import { AccountTypeIcon } from '@fragment/ui/src/components/Icon/AccountTypeIcon/AccountTypeIcon';
import { Entry as EntryIcon } from '@fragment/ui/src/components/Icon/svg/Entry';
import { EntryGroup as EntryGroupIcon } from '@fragment/ui/src/components/Icon/svg/EntryGroup';
import { Ledger as LedgerIcon } from '@fragment/ui/src/components/Icon/svg/Ledger';

import type { BreadcrumbElement } from './BreadcrumbUtils';
import { BreadcrumbComponent, BreadcrumbError } from './BreadcrumbUtils';

type LedgerAccountType = 'asset' | 'liability' | 'income' | 'expense';

type LedgerEntryAccount = {
  type: LedgerAccountType;
};

type LedgerEntry = {
  id: string;
  ik: string;
  description?: string | null;
  accounts: LedgerEntryAccount[];
};

export const BreadcrumbLedgerAccount = ({
  accountId,
  ledgerId,
  ledgerName,
  workspaceId,
  schemaKey,
}: {
  accountId: string;
  ledgerId: string;
  ledgerName: string;
  workspaceId: string;
  schemaKey: string;
}) => {
  const { context, token } = useCustomerApiContext();

  const variables = useMemo(
    () => ({ ledgerAccount: { id: accountId }, ledgerId }),
    [accountId, ledgerId]
  );

  const [{ data, fetching, error }, reExecuteQuery] = useGetLedgerAccountQuery({
    context,
    pause: !token,
    variables,
  });

  const { isError, result: ledgerAccount } = getResultOrError(
    data?.ledgerAccount,
    error
  );

  const retry = useCallback(() => {
    reExecuteQuery({ requestPolicy: 'network-only' });
  }, [reExecuteQuery]);

  const parentIds = useMemo(() => {
    if (fetching || isError) {
      return [];
    }

    const subpaths = getSubpaths(ledgerAccount.path).sort();
    const decodedLedgerId = decodeLedgerId(ledgerAccount.ledgerId).ledgerId;
    return subpaths.map((p) => ({
      name: getLastPathElement(p),
      id: encodeLedgerAccountId({
        ledgerId: decodedLedgerId,
        id: deterministicId({
          workspaceId,
          ledgerId: decodedLedgerId,
          ik: p,
        }),
      }),
    }));
  }, [ledgerAccount, workspaceId, fetching, isError]);

  const createElements = useCallback(() => {
    const baseURLComponents = [
      'w',
      workspaceId,
      'projects',
      'schema',
      schemaKey,
      ledgerId,
    ];
    const el: BreadcrumbElement[] = [];
    el.push({
      name: ledgerName,
      url: makePath([...baseURLComponents, 'home'], {
        absolute: true,
      }),
      icon: <LedgerIcon />,
    });
    if (parentIds.length > 0 && ledgerAccount) {
      parentIds.forEach((account, index) => {
        el.push({
          name: account.name,
          url: makePath([...baseURLComponents, 'accounts', account.id], {
            absolute: true,
          }),
          icon:
            index === 0 ? (
              <AccountTypeIcon type={ledgerAccount.type} />
            ) : undefined,
        });
      });
    } else {
      el.push({
        name: 'Accounts',
        url: makePath([...baseURLComponents, 'accounts'], {
          absolute: true,
        }),
      });
    }

    return el;
  }, [ledgerName, schemaKey, workspaceId, ledgerId, parentIds, ledgerAccount]);

  if (isError) {
    const baseURLComponents = [
      'w',
      workspaceId,
      'projects',
      schemaKey,
      ledgerId,
    ];

    return (
      <BreadcrumbError
        baseElement={{
          name: ledgerName,
          url: makePath([...baseURLComponents, 'home'], {
            absolute: true,
          }),
          icon: <LedgerIcon />,
        }}
        retry={retry}
        fetching={fetching}
      />
    );
  }

  return (
    <BreadcrumbComponent
      elements={createElements()}
      showLoadingText={fetching}
    />
  );
};

export const BreadcrumbNonSchemadLedgerAccount = ({
  accountId,
  ledgerId,
  ledgerName,
  workspaceId,
}: {
  accountId: string;
  ledgerId: string;
  ledgerName: string;
  workspaceId: string;
}) => {
  const { context, token } = useCustomerApiContext();

  const variables = useMemo(
    () => ({ ledgerAccount: { id: accountId }, ledgerId }),
    [accountId, ledgerId]
  );

  const [{ data, fetching, error }, reExecuteQuery] = useGetLedgerAccountQuery({
    context,
    pause: !token,
    variables,
  });

  const { isError, result: ledgerAccount } = getResultOrError(
    data?.ledgerAccount,
    error
  );

  const subpaths = getSubpaths(ledgerAccount?.path ?? '');

  const filter = {
    ledgerAccount: {
      in: subpaths.map((p) => ({ path: p, ledger: { id: ledgerId } })),
    },
  };

  const [
    {
      data: parentAccountData,
      fetching: fetchingParentAccounts,
      error: errorParentAccounts,
    },
    reExecuteQueryParentAccounts,
  ] = useGetLedgerAccountsQuery({
    context,
    pause: !token,
    variables: {
      ledger: { id: ledgerId },
      filter,
    },
  });

  const { isError: isParentError, result: parentAccounts } = getResultOrError(
    parentAccountData?.ledger?.ledgerAccounts,
    errorParentAccounts
  );

  const retry = useCallback(() => {
    reExecuteQuery({ requestPolicy: 'network-only' });
    reExecuteQueryParentAccounts({ requestPolicy: 'network-only' });
  }, [reExecuteQuery, reExecuteQueryParentAccounts]);

  const createElements = useCallback(() => {
    const baseURLComponents = ['w', workspaceId, 'projects', ledgerId];
    const el: BreadcrumbElement[] = [];
    el.push({
      name: ledgerName,
      url: makePath([...baseURLComponents, 'home'], {
        absolute: true,
      }),
      icon: <LedgerIcon />,
    });
    if (parentAccounts) {
      const listOfParentAccounts = [...parentAccounts.nodes].reverse();
      listOfParentAccounts.forEach((account, index) => {
        el.push({
          name: account.name ?? '',
          url: makePath([...baseURLComponents, 'accounts', account.id], {
            absolute: true,
          }),
          icon:
            index === 0 ? <AccountTypeIcon type={account.type} /> : undefined,
        });
      });
    } else {
      el.push({
        name: 'Accounts',
        url: makePath([...baseURLComponents, 'accounts'], {
          absolute: true,
        }),
      });
    }

    return el;
  }, [ledgerName, parentAccounts, workspaceId, ledgerId]);

  if (isError || isParentError) {
    const baseURLComponents = ['w', workspaceId, 'projects', ledgerId];
    return (
      <BreadcrumbError
        baseElement={{
          name: ledgerName,
          url: makePath([...baseURLComponents, 'home'], {
            absolute: true,
          }),
          icon: <LedgerIcon />,
        }}
        retry={retry}
        fetching={fetching}
      />
    );
  }

  return (
    <BreadcrumbComponent
      elements={createElements()}
      showLoadingText={fetchingParentAccounts || fetching}
    />
  );
};

export const BreadcrumbLedgerEntry = ({
  entryId,
  ledgerId,
  ledgerName,
  workspaceId,
  schemaKey,
}: {
  entryId: string;
  ledgerId: string;
  ledgerName: string;
  workspaceId: string;
  schemaKey?: string;
}) => {
  const { context, token } = useCustomerApiContext();

  const variables = useMemo(() => ({ entryId, ledgerId }), [entryId, ledgerId]);

  const [{ data, fetching, error }, reExecuteQuery] = useGetLedgerEntryQuery({
    context,
    pause: !token,
    variables,
  });

  const { isError, result: ledgerEntry } = getResultOrError(
    data?.ledgerEntry,
    error
  );

  const retry = useCallback(() => {
    reExecuteQuery({ requestPolicy: 'network-only' });
  }, [reExecuteQuery]);

  const createElements = useCallback(() => {
    const baseURLComponents = [
      'w',
      workspaceId,
      'projects',
      ...(schemaKey ? ['schema', schemaKey] : []),
      ledgerId,
    ];
    const el: BreadcrumbElement[] = [];
    el.push({
      name: ledgerName,
      url: makePath([...baseURLComponents, 'home'], {
        absolute: true,
      }),
      icon: <LedgerIcon />,
    });
    if (ledgerEntry) {
      const name =
        ledgerEntry.description === '' || !ledgerEntry.description
          ? ledgerEntry.ik
          : ledgerEntry.description;
      const accountTypes = ledgerEntry.lines.nodes.map(
        (line) => line.account.type
      );
      el.push({
        name,
        url: makePath([...baseURLComponents, 'entries', ledgerEntry.id], {
          absolute: true,
        }),
        icon: <EntryIcon types={accountTypes} />,
      });
    } else {
      el.push({
        name: 'Entries',
        url: makePath([...baseURLComponents, 'entries'], {
          absolute: true,
        }),
        icon: <EntryIcon types={['asset', 'liability', 'income', 'expense']} />,
      });
    }

    return el;
  }, [ledgerName, ledgerEntry, schemaKey, workspaceId, ledgerId]);

  if (isError) {
    const baseURLComponents = [
      'w',
      workspaceId,
      'projects',
      ...(schemaKey ? ['schema', schemaKey] : []),
      ledgerId,
    ];

    return (
      <BreadcrumbError
        baseElement={{
          name: ledgerName,
          url: makePath([...baseURLComponents, 'home'], {
            absolute: true,
          }),
          icon: <LedgerIcon />,
        }}
        retry={retry}
        fetching={fetching}
      />
    );
  }

  return (
    <BreadcrumbComponent
      elements={createElements()}
      showLoadingText={fetching}
    />
  );
};

export const BreadcrumbLedgerGroup = ({
  groupId,
  ledgerId,
  ledgerName,
  workspaceId,
  schemaKey,
}: {
  groupId: string;
  ledgerId: string;
  ledgerName: string;
  workspaceId: string;
  schemaKey?: string;
}) => {
  const { context, token } = useCustomerApiContext();

  const getGroupVariables = useMemo(() => {
    const [key, value] = groupId.split(':');
    return {
      ledger: {
        id: ledgerId,
      },
      ledgerEntryGroup: {
        key,
        value,
      },
    };
  }, [ledgerId, groupId]);
  const [{ data, fetching, error }, reExecuteQuery] =
    useGetLedgerEntryGroupQuery({
      variables: getGroupVariables,
      context,
      pause: !token,
    });

  const { isError, result: ledgerEntryGroup } = getResultOrError(
    data?.ledger?.ledgerEntryGroup,
    error
  );

  const retry = useCallback(() => {
    reExecuteQuery({ requestPolicy: 'network-only' });
  }, [reExecuteQuery]);

  const createElements = useCallback(() => {
    const baseURLComponents = [
      'w',
      workspaceId,
      'projects',
      ...(schemaKey ? ['schema', schemaKey] : []),
      ledgerId,
    ];
    const el: BreadcrumbElement[] = [];
    el.push({
      name: ledgerName,
      url: makePath([...baseURLComponents, 'home'], {
        absolute: true,
      }),
      icon: <LedgerIcon />,
    });
    if (ledgerEntryGroup) {
      el.push({
        name: `${ledgerEntryGroup.key}:${ledgerEntryGroup.value}`,
        url: makePath([...baseURLComponents, 'groups', groupId, 'entries'], {
          absolute: true,
        }),
        icon: <EntryGroupIcon />,
      });
    } else {
      el.push({
        name: 'Groups',
        url: makePath([...baseURLComponents, 'groups'], {
          absolute: true,
        }),
        icon: <EntryGroupIcon />,
      });
    }

    return el;
  }, [ledgerName, ledgerEntryGroup, schemaKey, workspaceId, ledgerId, groupId]);

  if (isError) {
    const baseURLComponents = [
      'w',
      workspaceId,
      'projects',
      ...(schemaKey ? ['schema', schemaKey] : []),
      ledgerId,
    ];
    return (
      <BreadcrumbError
        baseElement={{
          name: ledgerName,
          url: makePath([...baseURLComponents, 'home'], {
            absolute: true,
          }),
          icon: <LedgerIcon />,
        }}
        retry={retry}
        fetching={fetching}
      />
    );
  }

  return (
    <BreadcrumbComponent
      elements={createElements()}
      showLoadingText={fetching}
    />
  );
};

type BreadcrumbLedgersProps = {
  ledgerId: string;
  schemaKey?: string;
  workspaceId: string;
};

export const BreadcrumbLedgers = ({
  ledgerId,
  schemaKey,
  workspaceId,
}: BreadcrumbLedgersProps) => {
  const ledgerContext = useLedger();

  const ledgerName = useMemo(
    () => ledgerContext.ledger?.name ?? '',
    [ledgerContext]
  );

  const baseURLComponents = useMemo(
    () => [
      'w',
      workspaceId,
      'projects',
      ...(schemaKey ? ['schema', schemaKey] : []),
      ledgerId,
    ],
    [schemaKey, workspaceId, ledgerId]
  );

  const baseElement = useMemo(
    () => [
      {
        name: ledgerName,
        url: makePath([...baseURLComponents, 'home'], {
          absolute: true,
        }),
        icon: <LedgerIcon />,
      },
    ],
    [ledgerName, baseURLComponents]
  );

  const base = makePath(baseURLComponents);

  return (
    <Suspense
      fallback={<BreadcrumbComponent elements={baseElement} showLoadingText />}
    >
      <Router base={base} key={base}>
        <Switch>
          <DecodedParamsRoute path="/accounts/templates/:templatePath*">
            {({}) => (
              <BreadcrumbComponent
                elements={[
                  ...baseElement,
                  {
                    name: 'Accounts',
                    url: makePath([...baseURLComponents, 'accounts'], {
                      absolute: true,
                    }),
                  },
                  {
                    name: 'Template',
                  },
                ]}
              />
            )}
          </DecodedParamsRoute>
          <DecodedParamsRoute path="/accounts/:accountId/:rest*">
            {({ accountId }) =>
              schemaKey ? (
                <BreadcrumbLedgerAccount
                  accountId={accountId}
                  ledgerId={ledgerId}
                  ledgerName={ledgerName}
                  workspaceId={workspaceId}
                  schemaKey={schemaKey}
                />
              ) : (
                <BreadcrumbNonSchemadLedgerAccount
                  accountId={accountId}
                  ledgerId={ledgerId}
                  ledgerName={ledgerName}
                  workspaceId={workspaceId}
                />
              )
            }
          </DecodedParamsRoute>
          <DecodedParamsRoute path="/entries/:entryId/:rest*">
            {({ entryId }) => (
              <BreadcrumbLedgerEntry
                entryId={entryId}
                ledgerId={ledgerId}
                ledgerName={ledgerName}
                workspaceId={workspaceId}
                schemaKey={schemaKey}
              />
            )}
          </DecodedParamsRoute>
          <DecodedParamsRoute path="/groups/:groupId/:rest*">
            {({ groupId }) => (
              <BreadcrumbLedgerGroup
                groupId={groupId}
                ledgerId={ledgerId}
                ledgerName={ledgerName}
                workspaceId={workspaceId}
                schemaKey={schemaKey}
              />
            )}
          </DecodedParamsRoute>
          <Route path="/accounts">
            <BreadcrumbComponent
              elements={[
                ...baseElement,
                {
                  name: 'Accounts',
                  url: makePath([...baseURLComponents, 'accounts'], {
                    absolute: true,
                  }),
                },
              ]}
            />
          </Route>
          <Route path="/entries">
            <BreadcrumbComponent
              elements={[
                ...baseElement,
                {
                  name: 'Entries',
                  url: makePath([...baseURLComponents, 'entries'], {
                    absolute: true,
                  }),
                },
              ]}
            />
          </Route>
          <Route path="/groups">
            <BreadcrumbComponent
              elements={[
                ...baseElement,
                {
                  name: 'Groups',
                  url: makePath([...baseURLComponents, 'groups'], {
                    absolute: true,
                  }),
                },
              ]}
            />
          </Route>
          <Route>
            <BreadcrumbComponent elements={baseElement} />
          </Route>
        </Switch>
      </Router>
    </Suspense>
  );
};
